import { Flex, Stack } from '@mantine/core';
import { Text } from '@repo/foundations';
import { memo, useMemo } from 'react';
import type { IAiChat } from '../../../api/hooks/aiChats';
import { TIME_SAVINGS_CATEGORIES } from '../../../features/AiImpactDashboard/constants';
import { formatHoursToHm } from '../../../features/AiImpactDashboard/utils';
import { PopoverPreview } from './PopoverPreview';

const CATEGORY_MAP = TIME_SAVINGS_CATEGORIES.reduce<Record<string, string>>(
	(acc, category) => ({ ...acc, [category.key]: category.label }),
	{}
);

export const CategoriesColumn = memo(({ record }: { record: IAiChat }) => {
	const categories = useMemo(
		() => record.time_savings_categories ?? [],
		[record.time_savings_categories]
	);

	const timeSavingsBreakdown = useMemo(
		() => record.time_savings_breakdown ?? {},
		[record.time_savings_breakdown]
	);

	if (categories.length === 0) {
		return null;
	}

	const categoriesWithLabels = categories
		.map((categoryKey) => {
			const label = CATEGORY_MAP[categoryKey];
			if (!label) return null;
			const hours = timeSavingsBreakdown[categoryKey];
			return {
				key: categoryKey,
				label,
				hours: hours ?? 0,
			};
		})
		.filter(
			(item): item is { key: string; label: string; hours: number } =>
				item !== null
		);

	if (categoriesWithLabels.length === 0) {
		return null;
	}

	const previewLabel =
		categoriesWithLabels.length === 1
			? categoriesWithLabels[0].label
			: `${categoriesWithLabels.length} categories`;

	const previewContent = (
		<Text size="sm" color="text/primary/default">
			{previewLabel}
		</Text>
	);

	const dropdownContent = (
		<Stack spacing="sm">
			<Text size="xs" color="text/primary/default" weight="semibold">
				Categories
			</Text>
			<Stack spacing="xs">
				{categoriesWithLabels.map(({ key, label, hours }) => (
					<Flex key={key} justify="space-between" gap="sm">
						<Text size="sm" color="text/secondary/default">
							{label}
						</Text>
						<Text size="sm" color="text/secondary/default" weight="semibold">
							{formatHoursToHm(hours)}
						</Text>
					</Flex>
				))}
			</Stack>
		</Stack>
	);

	return <PopoverPreview preview={previewContent} dropdown={dropdownContent} />;
});
